import { useMutation } from "@tanstack/react-query";
import { ClipboardList, ChevronDown, Plus, ReceiptText } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { trackAnalytics } from "@/lib/analytics";
import { validateDiscountCode } from "@/lib/mockApi";
import { calculatePricing, loadSizes, materialTypes, serviceOptions, skipSizes, supplementalItems } from "@/lib/pricing";
import { formatCurrency } from "@/lib/utils";
import type { BookingState } from "@/types/booking";

type BookingSummaryProps = {
  state: BookingState;
  isLocked: boolean;
  updateState: (patch: Partial<BookingState>) => void;
};

export function BookingSummary({ state, isLocked, updateState }: BookingSummaryProps) {
  const [open, setOpen] = useState(false);
  const service = serviceOptions.find((item) => item.id === state.service)?.name;
  const total = state.pricingResult ? formatCurrency(state.pricingResult.totalPayable) : "TBC";
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 rounded-lg border-2 border-accent/10 bg-white p-4 text-left font-extrabold lg:hidden"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          <ReceiptText className="h-5 w-5 text-accent" />
          <span className="min-w-0">
            <span className="block text-sm">Order total</span>
            <span className="block truncate text-xs font-bold text-muted-foreground">
              {[service, state.postcode].filter(Boolean).join(" · ") || "Summary updates as you book"}
            </span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="text-lg font-black text-accent">{total}</span>
          <ChevronDown className={open ? "h-5 w-5 rotate-180 transition-transform" : "h-5 w-5 transition-transform"} />
        </span>
      </button>
      <div className={open ? "block lg:block" : "hidden lg:block"}>
        <SummaryCard state={state} isLocked={isLocked} updateState={updateState} />
      </div>
    </>
  );
}

function SummaryCard({ state, isLocked, updateState }: BookingSummaryProps) {
  const service = serviceOptions.find((item) => item.id === state.service)?.name;
  const selection =
    skipSizes.find((item) => item.id === state.skipSize)?.title ??
    loadSizes.find((item) => item.id === state.loadSize)?.title ??
    materialTypes.find((item) => item.id === state.materialType)?.title;
  const selectedExtras = supplementalItems.filter((item) => (state.supplementalItems[item.id] ?? 0) > 0);
  const bookingReference = state.bookingReference.trim();
  const orderStatusUrl = bookingReference ? `/orders/${encodeURIComponent(bookingReference)}` : "";
  const canApplyDiscount = Boolean(state.service && selection);
  const dateTime = [state.bookingDetails.date, state.bookingDetails.timeSlot].filter(Boolean).join(", ");

  return (
    <Card className="sticky top-6 rounded-lg">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Summary
          {state.paymentStatus !== "idle" ? <Badge>{state.paymentStatus}</Badge> : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <SummaryRow label="Postcode" value={state.postcode || "-"} />
        <SummaryRow label="Service" value={service || "-"} />
        <SummaryRow label="Size" value={selection || "-"} />
        <SummaryRow label="Date & time" value={dateTime || "-"} />
        {selectedExtras.length > 0 ? (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="font-extrabold">Extras</p>
              {selectedExtras.map((item) => {
                const quantity = state.supplementalItems[item.id] ?? 0;
                return (
                  <SummaryRow
                    key={item.id}
                    label={`${quantity}x ${item.title.toLowerCase()}`}
                    value={formatCurrency(item.price * quantity)}
                  />
                );
              })}
            </div>
          </>
        ) : null}
        {state.pricingResult ? (
          <>
            <Separator />
            <div className="space-y-2">
              <SummaryRow label="Service" value={formatCurrency(state.pricingResult.baseServicePrice)} />
              <SummaryRow label="Extras" value={formatCurrency(state.pricingResult.supplementalItemsTotal)} />
              {state.discountResult ? (
                <SummaryRow label="Discount" value={`-${formatCurrency(state.pricingResult.discount)}`} />
              ) : null}
              <SummaryRow label="VAT" value={formatCurrency(state.pricingResult.vat)} />
              <div className="flex items-center justify-between text-base font-black">
                <span>Total</span>
                <span>{formatCurrency(state.pricingResult.totalPayable)}</span>
              </div>
            </div>
          </>
        ) : null}
        {canApplyDiscount ? (
          <>
            <Separator />
            <DiscountCodeForm state={state} isLocked={isLocked} updateState={updateState} />
          </>
        ) : null}
        {bookingReference ? (
          <>
            <Separator />
            <Button asChild className="w-full">
              <a href={orderStatusUrl}>
                <ClipboardList className="h-4 w-4" />
                Booking Status
              </a>
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function DiscountCodeForm({ state, isLocked, updateState }: BookingSummaryProps) {
  const [code, setCode] = useState(state.discountCode);
  const [isOpen, setIsOpen] = useState(Boolean(state.discountCode));
  const discount = useMutation({
    mutationFn: () => validateDiscountCode(code, state),
    onSuccess: (result) => {
      updateState({ discountCode: result.code, discountResult: result });
      trackAnalytics("discount code applied", { code: result.code });
    },
  });

  function removeDiscountCode() {
    const nextState = { ...state, discountCode: "", discountResult: null };

    setCode("");
    discount.reset();
    updateState({
      discountCode: "",
      discountResult: null,
      pricingResult: state.pricingResult ? calculatePricing(nextState) : state.pricingResult,
    });
    trackAnalytics("discount code removed", { code: state.discountCode });
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 text-left font-black"
        aria-expanded={isOpen}
      >
        <span>Discount code</span>
        <Plus className={isOpen ? "h-5 w-5 rotate-45 text-primary transition-transform" : "h-5 w-5 text-primary transition-transform"} />
      </button>
      {isOpen ? (
        <>
          <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="SAVE10 or WASTE25"
              readOnly={isLocked}
              disabled={isLocked}
              className="min-w-0"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => discount.mutate()}
              disabled={isLocked || !code.trim() || discount.isPending}
              className="shrink-0"
            >
              Apply
            </Button>
          </div>
          {discount.error ? <p className="text-sm font-semibold text-destructive">{discount.error.message}</p> : null}
          {state.discountResult ? (
            <p className="text-sm font-bold text-accent">
              {state.discountResult.message}{" "}
              <button
                type="button"
                onClick={removeDiscountCode}
                disabled={isLocked}
                className="font-extrabold text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
              >
                Remove
              </button>
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-right font-bold">{value}</span>
    </div>
  );
}
